import { createFeature, createReducer, on } from '@ngrx/store';
import { OrganizationProjects } from '@models';
import { organizationProjectKey, organizationUserKey, projectUserKey } from '../join-keys';
import { WorkspaceActions, WorkspaceLoadPayload } from './workspace.actions';
import {
  initialWorkspaceState,
  orgProjectsAdapter,
  orgUsersAdapter,
  organizationsAdapter,
  projectUsersAdapter,
  projectsAdapter,
  WorkspaceState,
} from './workspace.models';

function resolveOrgForProject(orgProjects: OrganizationProjects[], projectId: string | null): string | null {
  if (!projectId) return null;
  return orgProjects.find((op) => op.projectId === projectId)?.organizationId ?? null;
}

function resolveSelection(
  state: WorkspaceState,
  payload: WorkspaceLoadPayload
): { selectedProjectId: string | null; selectedOrganizationId: string | null } {
  const projectIds = new Set(payload.projects.map((p) => p.id));
  const orgIds = new Set(payload.organizations.map((o) => o.id));

  const nextProjectId =
    (state.selectedProjectId && projectIds.has(state.selectedProjectId) && state.selectedProjectId) ||
    payload.projects[0]?.id ||
    null;

  let nextOrgId =
    (state.selectedOrganizationId && orgIds.has(state.selectedOrganizationId) && state.selectedOrganizationId) ||
    null;
  if (!nextOrgId) nextOrgId = resolveOrgForProject(payload.orgProjects, nextProjectId);

  return { selectedProjectId: nextProjectId, selectedOrganizationId: nextOrgId };
}

export const workspaceFeature = createFeature({
  name: 'workspace',
  reducer: createReducer(
    initialWorkspaceState,
    on(WorkspaceActions.init, (state): WorkspaceState => ({ ...state, error: null })),
    on(WorkspaceActions.loadStarted, (state): WorkspaceState => ({ ...state, status: 'loading', error: null })),
    on(WorkspaceActions.reset, (): WorkspaceState => initialWorkspaceState),
    on(WorkspaceActions.selectionRestored, (state, { projectId, organizationId }): WorkspaceState => ({
      ...state,
      selectedProjectId: projectId,
      selectedOrganizationId: organizationId,
    })),
    on(WorkspaceActions.selectProject, (state, { projectId, organizationId }): WorkspaceState => ({
      ...state,
      selectedProjectId: projectId,
      selectedOrganizationId:
        organizationId !== undefined
          ? organizationId
          : resolveOrgForProject(orgProjectsAdapter.getSelectors().selectAll(state.orgProjects), projectId),
    })),
    on(WorkspaceActions.loadSucceeded, (state, payload): WorkspaceState => {
      const selection = resolveSelection(state, payload);
      return {
        ...state,
        status: 'ready',
        error: null,
        projects: projectsAdapter.setAll(payload.projects, state.projects),
        organizations: organizationsAdapter.setAll(payload.organizations, state.organizations),
        orgProjects: orgProjectsAdapter.setAll(payload.orgProjects, state.orgProjects),
        projectUsers: projectUsersAdapter.setAll(payload.projectUsers, state.projectUsers),
        orgUsers: orgUsersAdapter.setAll(payload.orgUsers, state.orgUsers),
        ...selection,
      };
    }),
    on(WorkspaceActions.loadFailed, (state, { error }): WorkspaceState => ({ ...state, status: 'error', error })),
    on(WorkspaceActions.entitiesUpserted, (state, { entities }): WorkspaceState => ({
      ...state,
      projects: entities.projects ? projectsAdapter.upsertMany(entities.projects, state.projects) : state.projects,
      organizations: entities.organizations
        ? organizationsAdapter.upsertMany(entities.organizations, state.organizations)
        : state.organizations,
      orgProjects: entities.orgProjects
        ? orgProjectsAdapter.upsertMany(entities.orgProjects, state.orgProjects)
        : state.orgProjects,
      projectUsers: entities.projectUsers
        ? projectUsersAdapter.upsertMany(entities.projectUsers, state.projectUsers)
        : state.projectUsers,
      orgUsers: entities.orgUsers ? orgUsersAdapter.upsertMany(entities.orgUsers, state.orgUsers) : state.orgUsers,
    })),
    on(WorkspaceActions.entitiesRemoved, (state, { entities }): WorkspaceState => {
      return {
        ...state,
        projects: entities.projects
          ? projectsAdapter.removeMany(entities.projects.map((p) => p.id ?? ''), state.projects)
          : state.projects,
        organizations: entities.organizations
          ? organizationsAdapter.removeMany(entities.organizations.map((o) => o.id ?? ''), state.organizations)
          : state.organizations,
        orgProjects: entities.orgProjects
          ? orgProjectsAdapter.removeMany(
              entities.orgProjects.map((op) => organizationProjectKey(op) || `${op.organizationId}:${op.projectId}`),
              state.orgProjects
            )
          : state.orgProjects,
        projectUsers: entities.projectUsers
          ? projectUsersAdapter.removeMany(
              entities.projectUsers.map((pu) => projectUserKey(pu) || `${pu.projectId}:${pu.userId}`),
              state.projectUsers
            )
          : state.projectUsers,
        orgUsers: entities.orgUsers
          ? orgUsersAdapter.removeMany(
              entities.orgUsers.map((ou) => organizationUserKey(ou) || `${ou.organizationId}:${ou.userId}`),
              state.orgUsers
            )
          : state.orgUsers,
      };
    })
  ),
});
